import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Loader2,
  Users,
  Clock,
  Eye,
  MousePointer,
  AlertTriangle,
  Activity,
  Monitor,
  Smartphone,
  Tablet,
  ArrowLeft,
  RefreshCw,
  TrendingUp,
  TrendingDown,
  Timer,
  BarChart3,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Hourglass,
  ArrowUpDown,
  Flame,
  Zap
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Simple line chart component using SVG
function SimpleLineChart({ data, dataKey, color = "#00AEEF", height = 120 }) {
  if (!data || data.length === 0) return null;
  
  const values = data.map(d => d[dataKey] || 0);
  const max = Math.max(...values, 1);
  const width = 100;
  
  const points = values.map((v, i) => {
    const x = (i / (values.length - 1)) * width;
    const y = height - (v / max) * (height - 20);
    return `${x},${y}`;
  }).join(' ');
  
  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-full" preserveAspectRatio="none">
      <polyline
        fill="none"
        stroke={color}
        strokeWidth="2"
        points={points}
      />
      {/* Area fill */}
      <polygon
        fill={`${color}20`}
        points={`0,${height} ${points} ${width},${height}`}
      />
    </svg>
  );
}

// Format duration in human readable format
function formatDuration(seconds) {
  if (!seconds || seconds === 0) return "0s";
  
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.round(seconds % 60);
  
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  } else if (minutes > 0) {
    return `${minutes}m ${secs}s`;
  } else {
    return `${secs}s`;
  }
}

// Format relative time
function formatTimeAgo(dateString) {
  if (!dateString) return "Unknown";
  
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

export default function AdminAnalytics() {
  console.log("AdminAnalytics component loaded - VERSION WITH QUESTION TIME TAB");
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionActivities, setSessionActivities] = useState([]);
  const [activeTab, setActiveTab] = useState("overview");
  const [timeRange, setTimeRange] = useState("30");
  const [userTypeFilter, setUserTypeFilter] = useState("all");
  const [sessionsPage, setSessionsPage] = useState(1);
  const [sessionsPagination, setSessionsPagination] = useState({});
  const [questionTimeData, setQuestionTimeData] = useState(null);
  const [questionTimeLoading, setQuestionTimeLoading] = useState(false);
  const [sessionTimeData, setSessionTimeData] = useState(null);
  const [sessionTimeLoading, setSessionTimeLoading] = useState(false);
  const [dropOffData, setDropOffData] = useState(null);
  const [dropOffLoading, setDropOffLoading] = useState(false);
  const [regionFilter, setRegionFilter] = useState("all");
  const [questionSortField, setQuestionSortField] = useState("avgTime");
  const [questionSortDir, setQuestionSortDir] = useState("desc");
  
  useEffect(() => {
    const token = localStorage.getItem("adminToken") || localStorage.getItem("authToken") || localStorage.getItem("executiveToken");
    const isExecutivePath = window.location.pathname.startsWith('/executive');
    if (!token) {
      navigate(isExecutivePath ? "/executive" : "/admin/login");
      return;
    }
    fetchAnalytics();
  }, [navigate, timeRange]);
  
  useEffect(() => {
    if (activeTab === "sessions") {
      fetchSessions();
    }
    if (activeTab === "question-time") {
      fetchQuestionTimeAnalytics();
    }
    if (activeTab === "session-time") {
      fetchSessionTimeAnalytics();
    }
    if (activeTab === "drop-off") {
      fetchDropOffAnalytics();
    }
  }, [activeTab, sessionsPage, userTypeFilter, timeRange, regionFilter]);
  
  const getAuthHeaders = () => {
    const token = localStorage.getItem("adminToken") || localStorage.getItem("authToken") || localStorage.getItem("executiveToken");
    return { headers: { Authorization: `Bearer ${token}` } };
  };
  
  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      // Fetch both dashboard analytics and drop-off data for the summary
      const [analyticsRes, dropOffRes] = await Promise.all([
        axios.get(`${API}/analytics/dashboard?days=${timeRange}`, getAuthHeaders()),
        axios.get(`${API}/analytics/drop-off`, getAuthHeaders()).catch(() => ({ data: null }))
      ]);
      setAnalytics(analyticsRes.data);
      if (dropOffRes.data) {
        setDropOffData(dropOffRes.data);
      }
    } catch (err) {
      console.error("Error fetching analytics:", err);
      if (err.response?.status === 401) {
        const isExecutivePath = window.location.pathname.startsWith('/executive');
        navigate(isExecutivePath ? "/executive" : "/admin/login");
      } else {
        toast.error("Failed to load analytics data");
      }
    } finally {
      setLoading(false);
    }
  };
  
  const fetchSessions = async () => {
    try {
      const params = new URLSearchParams({
        page: sessionsPage,
        limit: 20,
        days: timeRange
      });
      if (userTypeFilter !== "all") {
        params.append("user_type", userTypeFilter);
      }
      if (regionFilter !== "all") {
        params.append("region", regionFilter);
      }
      
      const response = await axios.get(`${API}/analytics/sessions?${params}`, getAuthHeaders());
      setSessions(response.data.sessions);
      setSessionsPagination(response.data.pagination);
    } catch (err) {
      console.error("Error fetching sessions:", err);
      toast.error("Failed to load sessions");
    }
  };
  
  const fetchSessionActivities = async (sessionId) => {
    try {
      const response = await axios.get(`${API}/analytics/sessions/${sessionId}/activities`, getAuthHeaders());
      setSessionActivities(response.data.activities);
    } catch (err) {
      console.error("Error fetching session activities:", err);
      toast.error("Failed to load session details");
    }
  };
  
  const fetchQuestionTimeAnalytics = async () => {
    try {
      setQuestionTimeLoading(true);
      const params = regionFilter !== "all" ? `?region=${regionFilter}` : "";
      const response = await axios.get(`${API}/analytics/question-time${params}`, getAuthHeaders());
      setQuestionTimeData(response.data);
    } catch (err) {
      console.error("Error fetching question time analytics:", err);
      toast.error("Failed to load question time analytics");
    } finally {
      setQuestionTimeLoading(false);
    }
  };

  const fetchSessionTimeAnalytics = async () => {
    try {
      setSessionTimeLoading(true);
      const params = regionFilter !== "all" ? `?region=${regionFilter}` : "";
      const response = await axios.get(`${API}/analytics/session-time${params}`, getAuthHeaders());
      setSessionTimeData(response.data);
    } catch (err) {
      console.error("Error fetching session time analytics:", err);
      toast.error("Failed to load session time analytics");
    } finally {
      setSessionTimeLoading(false);
    }
  };

  const fetchDropOffAnalytics = async () => {
    try {
      setDropOffLoading(true);
      const params = regionFilter !== "all" ? `?region=${regionFilter}` : "";
      const response = await axios.get(`${API}/analytics/drop-off${params}`, getAuthHeaders());
      setDropOffData(response.data);
    } catch (err) {
      console.error("Error fetching drop-off analytics:", err);
      toast.error("Failed to load drop-off analytics");
    } finally {
      setDropOffLoading(false);
    }
  };
  
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAnalytics();
    if (activeTab === "sessions") {
      await fetchSessions();
    }
    if (activeTab === "question-time") {
      await fetchQuestionTimeAnalytics();
    }
    if (activeTab === "session-time") {
      await fetchSessionTimeAnalytics();
    }
    if (activeTab === "drop-off") {
      await fetchDropOffAnalytics();
    }
    setRefreshing(false);
    toast.success("Data refreshed");
  };
  
  const handleSessionClick = async (session) => {
    setSelectedSession(session);
    await fetchSessionActivities(session.sessionId);
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8f9fa]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#00AEEF] mx-auto mb-3" />
          <p className="text-[#6b7280] text-sm">Loading analytics...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-[#f8f9fa]">
      {/* Header */}
      <header className="py-4 px-6 bg-white border-b border-[#e5e7eb]" data-testid="analytics-header">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to={window.location.pathname.startsWith('/executive') ? '/executive/dashboard' : '/admin'}>
              <Button variant="ghost" size="sm" className="gap-2">
                <ArrowLeft className="w-4 h-4" />
                Back to Dashboard
              </Button>
            </Link>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-[#00AEEF] to-[#0077B5] rounded-lg flex items-center justify-center">
                <Activity className="w-5 h-5 text-white" />
              </div>
              <div>
                <span className="text-lg font-bold text-[#3b3b3b]">
                  User Analytics
                </span>
                <p className="text-xs text-[#6b7280]">Track user behavior & engagement</p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Select value={timeRange} onValueChange={setTimeRange}>
              <SelectTrigger className="w-[140px]" data-testid="time-range-select">
                <SelectValue placeholder="Time Range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="14">Last 14 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
              </SelectContent>
            </Select>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleRefresh}
              disabled={refreshing}
              className="gap-2"
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto py-8 px-6">
        {/* Summary Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          <Card className="bg-white border-0 shadow-sm" data-testid="stat-sessions">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#00AEEF]/10 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-[#00AEEF]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#3b3b3b]">{analytics?.summary?.totalSessions || 0}</p>
                  <p className="text-xs text-[#6b7280]">Total Sessions</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white border-0 shadow-sm" data-testid="stat-active">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#63C465]/10 rounded-lg flex items-center justify-center">
                  <Activity className="w-5 h-5 text-[#63C465]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#63C465]">{analytics?.summary?.activeSessions || 0}</p>
                  <p className="text-xs text-[#6b7280]">Active Now</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white border-0 shadow-sm" data-testid="stat-duration">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Zap className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#3b3b3b]">
                    {dropOffData?.summary?.avgActiveHours 
                      ? `${dropOffData.summary.avgActiveHours.toFixed(1)}h`
                      : formatDuration(analytics?.summary?.avgSessionDuration || 0)
                    }
                  </p>
                  <p className="text-xs text-[#6b7280]">Avg Active Time</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white border-0 shadow-sm" data-testid="stat-pageviews">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Eye className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#3b3b3b]">{analytics?.summary?.totalPageViews || 0}</p>
                  <p className="text-xs text-[#6b7280]">Page Views</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white border-0 shadow-sm" data-testid="stat-actions">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <MousePointer className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#3b3b3b]">{analytics?.summary?.totalActions || 0}</p>
                  <p className="text-xs text-[#6b7280]">Actions</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white border-0 shadow-sm" data-testid="stat-errors">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-red-600">{analytics?.summary?.totalErrors || 0}</p>
                  <p className="text-xs text-[#6b7280]">Errors</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-6 bg-white">
            <TabsTrigger value="overview" className="data-[state=active]:bg-[#00AEEF]/10 data-[state=active]:text-[#00AEEF]">
              <BarChart3 className="w-4 h-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="session-time" className="data-[state=active]:bg-[#00AEEF]/10 data-[state=active]:text-[#00AEEF]">
              <Timer className="w-4 h-4 mr-2" />
              Session Time
            </TabsTrigger>
            <TabsTrigger value="drop-off" className="data-[state=active]:bg-[#00AEEF]/10 data-[state=active]:text-[#00AEEF]">
              <TrendingDown className="w-4 h-4 mr-2" />
              Drop-off Analysis
            </TabsTrigger>
            <TabsTrigger value="question-time" className="data-[state=active]:bg-[#00AEEF]/10 data-[state=active]:text-[#00AEEF]">
              <Hourglass className="w-4 h-4 mr-2" />
              Question Time
            </TabsTrigger>
            <TabsTrigger value="sessions" className="data-[state=active]:bg-[#00AEEF]/10 data-[state=active]:text-[#00AEEF]">
              <Users className="w-4 h-4 mr-2" />
              User Sessions
            </TabsTrigger>
            <TabsTrigger value="errors" className="data-[state=active]:bg-[#00AEEF]/10 data-[state=active]:text-[#00AEEF]">
              <AlertTriangle className="w-4 h-4 mr-2" />
              Errors & Issues
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Activity Chart */}
              <Card className="bg-white border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-[#3b3b3b] flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-[#00AEEF]" />
                    Daily Activity
                  </CardTitle>
                  <CardDescription>Sessions and page views over time</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[200px]">
                    {analytics?.dailyActivity && analytics.dailyActivity.length > 0 ? (
                      <div className="space-y-4">
                        <div>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs text-[#6b7280]">Sessions</span>
                            <span className="text-xs font-medium text-[#00AEEF]">
                              {analytics.dailyActivity[analytics.dailyActivity.length - 1]?.sessions || 0} today
                            </span>
                          </div>
                          <div className="h-[80px]">
                            <SimpleLineChart data={analytics.dailyActivity} dataKey="sessions" color="#00AEEF" height={80} />
                          </div>
                        </div>
                        <div>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs text-[#6b7280]">Page Views</span>
                            <span className="text-xs font-medium text-[#63C465]">
                              {analytics.dailyActivity[analytics.dailyActivity.length - 1]?.pageViews || 0} today
                            </span>
                          </div>
                          <div className="h-[80px]">
                            <SimpleLineChart data={analytics.dailyActivity} dataKey="pageViews" color="#63C465" height={80} />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center h-full text-[#6b7280]">
                        No activity data yet
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* User Types Distribution */}
              <Card className="bg-white border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-[#3b3b3b] flex items-center gap-2">
                    <Users className="w-5 h-5 text-[#00AEEF]" />
                    Sessions by User Type
                  </CardTitle>
                  <CardDescription>Distribution of sessions by user role</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {analytics?.sessionsByType && Object.keys(analytics.sessionsByType).length > 0 ? (
                      Object.entries(analytics.sessionsByType).map(([type, count]) => {
                        const total = Object.values(analytics.sessionsByType).reduce((a, b) => a + b, 0);
                        const percentage = total > 0 ? Math.round((count / total) * 100) : 0;
                        return (
                          <div key={type} className="space-y-1">
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-[#3b3b3b] capitalize">{type || 'Unknown'}</span>
                              <span className="text-[#6b7280]">{count} ({percentage}%)</span>
                            </div>
                            <Progress value={percentage} className="h-2" />
                          </div>
                        );
                      })
                    ) : (
                      <div className="text-center py-8 text-[#6b7280]">
                        No session data yet
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Device Distribution */}
              <Card className="bg-white border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-[#3b3b3b] flex items-center gap-2">
                    <Monitor className="w-5 h-5 text-[#00AEEF]" />
                    Sessions by Device
                  </CardTitle>
                  <CardDescription>How users access the platform</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4">
                    {['desktop', 'mobile', 'tablet'].map(device => {
                      const count = analytics?.sessionsByDevice?.[device] || 0;
                      const Icon = device === 'desktop' ? Monitor : device === 'mobile' ? Smartphone : Tablet;
                      return (
                        <div key={device} className="text-center p-4 bg-[#f8f9fa] rounded-lg">
                          <Icon className="w-8 h-8 mx-auto mb-2 text-[#00AEEF]" />
                          <p className="text-2xl font-bold text-[#3b3b3b]">{count}</p>
                          <p className="text-xs text-[#6b7280] capitalize">{device}</p>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Top Pages */}
              <Card className="bg-white border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-[#3b3b3b] flex items-center gap-2">
                    <Eye className="w-5 h-5 text-[#00AEEF]" />
                    Top Pages
                  </CardTitle>
                  <CardDescription>Most visited pages</CardDescription>
                </CardHeader>
                <CardContent>
                  {analytics?.topPages && analytics.topPages.length > 0 ? (
                    <div className="space-y-2">
                      {analytics.topPages.slice(0, 5).map((page, index) => (
                        <div key={index} className="flex items-center justify-between p-2 bg-[#f8f9fa] rounded-lg">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-[#3b3b3b] truncate">
                              {page.title || page.url || 'Unknown Page'}
                            </p>
                            <p className="text-xs text-[#6b7280] truncate">{page.url}</p>
                          </div>
                          <div className="text-right ml-4">
                            <p className="text-sm font-bold text-[#00AEEF]">{page.views}</p>
                            <p className="text-xs text-[#6b7280]">{formatDuration(page.avgDuration)} avg</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-[#6b7280]">
                      No page view data yet
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Session Time Analytics Tab */}
          <TabsContent value="session-time">
            {sessionTimeLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-[#00AEEF]" />
              </div>
            ) : sessionTimeData ? (
              <div className="space-y-6">
                {/* Summary Stats */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <Card className="bg-white border-0 shadow-sm">
                    <CardContent className="pt-4 pb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#00AEEF]/10 rounded-lg flex items-center justify-center">
                          <Timer className="w-5 h-5 text-[#00AEEF]" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-[#3b3b3b]">{sessionTimeData.summary?.formattedAvgTime || '0m'}</p>
                          <p className="text-xs text-[#6b7280]">Avg Completion Time</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-white border-0 shadow-sm">
                    <CardContent className="pt-4 pb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#63C465]/10 rounded-lg flex items-center justify-center">
                          <TrendingUp className="w-5 h-5 text-[#63C465]" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-[#3b3b3b]">{sessionTimeData.summary?.completionRate || 0}%</p>
                          <p className="text-xs text-[#6b7280]">Completion Rate</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-white border-0 shadow-sm">
                    <CardContent className="pt-4 pb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                          <Users className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-[#3b3b3b]">{sessionTimeData.summary?.totalSessions?.toLocaleString() || 0}</p>
                          <p className="text-xs text-[#6b7280]">Total Sessions</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-white border-0 shadow-sm">
                    <CardContent className="pt-4 pb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                          <Clock className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-[#3b3b3b]">{sessionTimeData.summary?.formattedMedianTime || '0m'}</p>
                          <p className="text-xs text-[#6b7280]">Median Time</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Region Filter */}
                <div className="flex justify-end">
                  <Select value={regionFilter} onValueChange={setRegionFilter}>
                    <SelectTrigger className="w-[160px]">
                      <SelectValue placeholder="Filter by Region" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Regions</SelectItem>
                      <SelectItem value="jamaica">🇯🇲 Jamaica</SelectItem>
                      <SelectItem value="canada">🇨🇦 Canada</SelectItem>
                      <SelectItem value="usa">🇺🇸 USA</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Status Breakdown */}
                  <Card className="bg-white border-0 shadow-sm">
                    <CardHeader>
                      <CardTitle className="text-[#3b3b3b] flex items-center gap-2">
                        <Activity className="w-5 h-5 text-[#00AEEF]" />
                        Session Status
                      </CardTitle>
                      <CardDescription>Breakdown by completion status</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {sessionTimeData.statusBreakdown && Object.entries(sessionTimeData.statusBreakdown).map(([status, count]) => {
                          const total = sessionTimeData.summary?.totalSessions || 1;
                          const percentage = (count / total) * 100;
                          const colorClass = status === 'completed' ? 'from-[#63C465] to-[#3da33f]' : 
                                            status === 'started' ? 'from-[#00AEEF] to-[#0077B5]' : 
                                            'from-gray-400 to-gray-500';
                          return (
                            <div key={status} className="space-y-1">
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-[#3b3b3b] capitalize font-medium">{status}</span>
                                <span className="text-[#6b7280]">{count} ({percentage.toFixed(1)}%)</span>
                              </div>
                              <div className="h-3 bg-[#f0f0f0] rounded-full overflow-hidden">
                                <div 
                                  className={`h-full bg-gradient-to-r ${colorClass} rounded-full transition-all`}
                                  style={{ width: `${percentage}%` }}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Time Distribution */}
                  <Card className="bg-white border-0 shadow-sm">
                    <CardHeader>
                      <CardTitle className="text-[#3b3b3b] flex items-center gap-2">
                        <Clock className="w-5 h-5 text-[#00AEEF]" />
                        Time Distribution
                      </CardTitle>
                      <CardDescription>How long users take to complete</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {sessionTimeData.timeDistribution && Object.entries(sessionTimeData.timeDistribution).map(([bucket, count]) => {
                          const maxCount = Math.max(...Object.values(sessionTimeData.timeDistribution), 1);
                          const percentage = (count / maxCount) * 100;
                          return (
                            <div key={bucket} className="space-y-1">
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-[#3b3b3b] font-medium">{bucket}</span>
                                <span className="text-[#6b7280]">{count} users</span>
                              </div>
                              <div className="h-2 bg-[#f0f0f0] rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-gradient-to-r from-purple-500 to-purple-600 rounded-full transition-all"
                                  style={{ width: `${percentage}%` }}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Drop-off Points */}
                  <Card className="bg-white border-0 shadow-sm">
                    <CardHeader>
                      <CardTitle className="text-[#3b3b3b] flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-orange-500" />
                        Drop-off Analysis
                      </CardTitle>
                      <CardDescription>Where users abandon the assessment</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {sessionTimeData.dropOffPoints && Object.entries(sessionTimeData.dropOffPoints)
                          .filter(([_, count]) => count > 0)
                          .sort((a, b) => b[1] - a[1])
                          .map(([point, count]) => {
                            const isCompleted = point.includes('100%') || point.includes('complete');
                            return (
                              <div key={point} className={`flex items-center justify-between p-3 rounded-lg ${isCompleted ? 'bg-green-50' : 'bg-orange-50'}`}>
                                <span className={`text-sm font-medium ${isCompleted ? 'text-green-700' : 'text-orange-700'}`}>{point}</span>
                                <span className={`text-sm font-bold ${isCompleted ? 'text-green-600' : 'text-orange-600'}`}>{count}</span>
                              </div>
                            );
                        })}
                      </div>
                    </CardContent>
                  </Card>

                  {/* By Region */}
                  <Card className="bg-white border-0 shadow-sm">
                    <CardHeader>
                      <CardTitle className="text-[#3b3b3b] flex items-center gap-2">
                        <Users className="w-5 h-5 text-[#00AEEF]" />
                        Time by Region
                      </CardTitle>
                      <CardDescription>Average completion time per region</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {sessionTimeData.byRegion && Object.entries(sessionTimeData.byRegion).map(([region, data]) => {
                          const maxTime = Math.max(...Object.values(sessionTimeData.byRegion).map(d => d.avgDuration || 0), 1);
                          const percentage = (data.avgDuration / maxTime) * 100;
                          const flagEmoji = region === 'jamaica' ? '🇯🇲' : region === 'canada' ? '🇨🇦' : region === 'usa' ? '🇺🇸' : '🌍';
                          return (
                            <div key={region} className="space-y-1">
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-[#3b3b3b] capitalize font-medium">{flagEmoji} {region}</span>
                                <span className="text-[#6b7280]">{data.formattedAvg}</span>
                              </div>
                              <div className="h-3 bg-[#f0f0f0] rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-gradient-to-r from-[#00AEEF] to-[#0077B5] rounded-full transition-all"
                                  style={{ width: `${percentage}%` }}
                                />
                              </div>
                              <p className="text-xs text-[#6b7280]">{data.totalSessions} sessions</p>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Progress Distribution */}
                <Card className="bg-white border-0 shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-[#3b3b3b] flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-[#00AEEF]" />
                      Progress Distribution
                    </CardTitle>
                    <CardDescription>Where users are in their assessment journey</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-4">
                      {sessionTimeData.progressDistribution && Object.entries(sessionTimeData.progressDistribution).map(([bucket, count]) => {
                        const bgColor = bucket === '100%' ? 'bg-green-100 border-green-300' :
                                       bucket === '75-99%' ? 'bg-blue-100 border-blue-300' :
                                       bucket === '50-74%' ? 'bg-yellow-100 border-yellow-300' :
                                       bucket === '25-49%' ? 'bg-orange-100 border-orange-300' :
                                       'bg-gray-100 border-gray-300';
                        const textColor = bucket === '100%' ? 'text-green-700' :
                                         bucket === '75-99%' ? 'text-blue-700' :
                                         bucket === '50-74%' ? 'text-yellow-700' :
                                         bucket === '25-49%' ? 'text-orange-700' :
                                         'text-gray-700';
                        return (
                          <div key={bucket} className={`px-4 py-3 rounded-lg border-2 ${bgColor}`}>
                            <p className={`text-2xl font-bold ${textColor}`}>{count}</p>
                            <p className={`text-sm ${textColor}`}>{bucket}</p>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>

                {/* Min/Max Times */}
                <Card className="bg-white border-0 shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-[#3b3b3b]">Completion Time Range</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <div className="text-center">
                        <p className="text-sm text-[#6b7280]">Fastest</p>
                        <p className="text-xl font-bold text-[#63C465]">{formatDuration(sessionTimeData.summary?.minCompletionTime || 0)}</p>
                      </div>
                      <div className="flex-1 mx-8">
                        <div className="h-2 bg-gradient-to-r from-[#63C465] via-[#00AEEF] to-orange-500 rounded-full"></div>
                      </div>
                      <div className="text-center">
                        <p className="text-sm text-[#6b7280]">Slowest</p>
                        <p className="text-xl font-bold text-orange-500">{formatDuration(sessionTimeData.summary?.maxCompletionTime || 0)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ) : (
              <div className="text-center py-12">
                <Timer className="w-12 h-12 mx-auto mb-4 text-[#6b7280] opacity-40" />
                <p className="text-[#6b7280]">No session time data available yet</p>
                <p className="text-sm text-[#6b7280] mt-1">Data will appear as candidates complete assessments</p>
              </div>
            )}
          </TabsContent>

          {/* Drop-off Analysis Tab */}
          <TabsContent value="drop-off">
            {dropOffLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-[#00AEEF]" />
              </div>
            ) : dropOffData ? (
              <div className="space-y-6">
                {/* Summary Stats */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <Card className="bg-gradient-to-br from-blue-50 to-white border-0 shadow-sm">
                    <CardContent className="pt-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-[#6b7280]">Avg Active Time</p>
                          <p className="text-2xl font-bold text-[#3b3b3b]">
                            {dropOffData.summary?.avgActiveHours?.toFixed(1) || 0}h
                          </p>
                        </div>
                        <Zap className="w-8 h-8 text-blue-500" />
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-orange-50 to-white border-0 shadow-sm">
                    <CardContent className="pt-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-[#6b7280]">Avg Elapsed Time</p>
                          <p className="text-2xl font-bold text-[#3b3b3b]">
                            {dropOffData.summary?.avgElapsedHours?.toFixed(1) || 0}h
                          </p>
                        </div>
                        <Clock className="w-8 h-8 text-orange-500" />
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-green-50 to-white border-0 shadow-sm">
                    <CardContent className="pt-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-[#6b7280]">Avg Efficiency</p>
                          <p className="text-2xl font-bold text-[#3b3b3b]">
                            {dropOffData.summary?.avgEfficiency?.toFixed(1) || 0}%
                          </p>
                        </div>
                        <TrendingUp className="w-8 h-8 text-green-500" />
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-purple-50 to-white border-0 shadow-sm">
                    <CardContent className="pt-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-[#6b7280]">Total Analyzed</p>
                          <p className="text-2xl font-bold text-[#3b3b3b]">
                            {dropOffData.summary?.totalCandidates || 0}
                          </p>
                        </div>
                        <Users className="w-8 h-8 text-purple-500" />
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Region Filter */}
                <div className="flex justify-end">
                  <Select value={regionFilter} onValueChange={setRegionFilter}>
                    <SelectTrigger className="w-[160px]">
                      <SelectValue placeholder="Select Region" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Regions</SelectItem>
                      <SelectItem value="jamaica">🇯🇲 Jamaica</SelectItem>
                      <SelectItem value="usa">🇺🇸 USA</SelectItem>
                      <SelectItem value="canada">🇨🇦 Canada</SelectItem>
                      <SelectItem value="barbados">🇧🇧 Barbados</SelectItem>
                      <SelectItem value="trinidad_tobago">🇹🇹 Trinidad & Tobago</SelectItem>
                      <SelectItem value="panama">🇵🇦 Panama</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Explanation Card */}
                <Card className="bg-amber-50 border-amber-200">
                  <CardContent className="pt-4">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                      <div>
                        <p className="font-medium text-amber-800">Understanding Active vs Elapsed Time</p>
                        <p className="text-sm text-amber-700 mt-1">
                          <strong>Active Time:</strong> Actual time spent answering questions (gaps &lt; 30 min between responses).<br/>
                          <strong>Elapsed Time:</strong> Total time from first response to last response.<br/>
                          <strong>Efficiency:</strong> Active Time ÷ Elapsed Time × 100%. Low efficiency = many breaks/drop-offs.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Efficiency Distribution */}
                <Card className="bg-white border-0 shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-[#3b3b3b]">Efficiency Distribution</CardTitle>
                    <CardDescription>How efficiently candidates are completing assessments</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="text-center p-4 bg-green-50 rounded-lg">
                        <p className="text-3xl font-bold text-green-600">
                          {dropOffData.efficiencyDistribution?.high || 0}
                        </p>
                        <p className="text-sm text-green-700">High Efficiency (≥70%)</p>
                        <p className="text-xs text-green-600 mt-1">Focused, minimal breaks</p>
                      </div>
                      <div className="text-center p-4 bg-yellow-50 rounded-lg">
                        <p className="text-3xl font-bold text-yellow-600">
                          {dropOffData.efficiencyDistribution?.medium || 0}
                        </p>
                        <p className="text-sm text-yellow-700">Medium Efficiency (30-70%)</p>
                        <p className="text-xs text-yellow-600 mt-1">Some breaks taken</p>
                      </div>
                      <div className="text-center p-4 bg-red-50 rounded-lg">
                        <p className="text-3xl font-bold text-red-600">
                          {dropOffData.efficiencyDistribution?.low || 0}
                        </p>
                        <p className="text-sm text-red-700">Low Efficiency (&lt;30%)</p>
                        <p className="text-xs text-red-600 mt-1">Many drop-offs/breaks</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Focused Participants View - NEW SECTION */}
                {dropOffData.focusedParticipants && dropOffData.focusedParticipants.count > 0 && (
                  <Card className="bg-white border-0 shadow-sm border-l-4 border-l-green-500">
                    <CardHeader>
                      <CardTitle className="text-[#3b3b3b] flex items-center gap-2">
                        <Zap className="w-5 h-5 text-green-500" />
                        Focused Participants View
                      </CardTitle>
                      <CardDescription>
                        Statistics for candidates with 70%+ efficiency (minimal breaks, focused effort)
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      {/* Key Stats Grid */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                        <div className="bg-green-50 rounded-lg p-4 text-center">
                          <p className="text-3xl font-bold text-green-600">
                            {dropOffData.focusedParticipants.count}
                          </p>
                          <p className="text-sm text-green-700">Focused Participants</p>
                        </div>
                        <div className="bg-blue-50 rounded-lg p-4 text-center">
                          <p className="text-3xl font-bold text-blue-600">
                            {dropOffData.focusedParticipants.avgActiveMinutes || 0}
                            <span className="text-lg font-normal">min</span>
                          </p>
                          <p className="text-sm text-blue-700">Avg Active Time</p>
                          <p className="text-xs text-blue-500">({dropOffData.focusedParticipants.avgActiveHours}h)</p>
                        </div>
                        <div className="bg-purple-50 rounded-lg p-4 text-center">
                          <p className="text-3xl font-bold text-purple-600">
                            {dropOffData.focusedParticipants.medianActiveMinutes || 0}
                            <span className="text-lg font-normal">min</span>
                          </p>
                          <p className="text-sm text-purple-700">Median Active Time</p>
                          <p className="text-xs text-purple-500">({dropOffData.focusedParticipants.medianActiveHours}h)</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4 text-center">
                          <div className="flex justify-center gap-4 text-sm">
                            <div>
                              <p className="font-bold text-green-600">{dropOffData.focusedParticipants.minActiveHours}h</p>
                              <p className="text-xs text-gray-500">Fastest</p>
                            </div>
                            <div className="border-l border-gray-300"></div>
                            <div>
                              <p className="font-bold text-orange-600">{dropOffData.focusedParticipants.maxActiveHours}h</p>
                              <p className="text-xs text-gray-500">Slowest</p>
                            </div>
                          </div>
                          <p className="text-sm text-gray-700 mt-1">Active Time Range</p>
                        </div>
                      </div>

                      {/* Key Insight Box */}
                      <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-4 mb-6">
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                            <TrendingUp className="w-4 h-4 text-green-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-800">Key Insight</p>
                            <p className="text-sm text-gray-600 mt-1">
                              <strong>{dropOffData.focusedParticipants.count}</strong> participants ({Math.round((dropOffData.focusedParticipants.count / dropOffData.summary.totalCandidates) * 100)}% of total) completed with focused effort. 
                              Their median completion time of <strong>{dropOffData.focusedParticipants.medianActiveMinutes} minutes</strong> suggests 
                              {dropOffData.focusedParticipants.medianActiveMinutes <= 90 
                                ? " the assessment fits within the 60-90 minute guidance when taken without interruptions."
                                : " focused participants may need slightly more than the 60-90 minute guidance."}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Focused Candidates Table */}
                      {dropOffData.focusedParticipants.candidates && dropOffData.focusedParticipants.candidates.length > 0 && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-2">Focused Participants (sorted by active time)</p>
                          <div className="max-h-[250px] overflow-y-auto border rounded-lg">
                            <Table>
                              <TableHeader>
                                <TableRow className="bg-gray-50">
                                  <TableHead>Name</TableHead>
                                  <TableHead>Region</TableHead>
                                  <TableHead className="text-right">Active Time</TableHead>
                                  <TableHead className="text-right">Efficiency</TableHead>
                                  <TableHead className="text-right">Responses</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {dropOffData.focusedParticipants.candidates.map((candidate, idx) => (
                                  <TableRow key={idx}>
                                    <TableCell className="font-medium">{candidate.name}</TableCell>
                                    <TableCell>
                                      <Badge variant="outline" className="capitalize text-xs">
                                        {candidate.region || 'N/A'}
                                      </Badge>
                                    </TableCell>
                                    <TableCell className="text-right">
                                      <span className="font-medium text-green-600">
                                        {Math.round(candidate.activeHours * 60)}min
                                      </span>
                                      <span className="text-xs text-gray-400 ml-1">({candidate.activeHours}h)</span>
                                    </TableCell>
                                    <TableCell className="text-right">
                                      <Badge className="bg-green-100 text-green-700">{candidate.efficiency}%</Badge>
                                    </TableCell>
                                    <TableCell className="text-right text-gray-600">{candidate.responseCount}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Drop-off Candidates Table */}
                <Card className="bg-white border-0 shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-[#3b3b3b] flex items-center gap-2">
                      <TrendingDown className="w-5 h-5 text-red-500" />
                      Candidates with Most Drop-offs
                    </CardTitle>
                    <CardDescription>Candidates who took the longest breaks during assessment</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Region</TableHead>
                          <TableHead>Progress</TableHead>
                          <TableHead className="text-right">Active Time</TableHead>
                          <TableHead className="text-right">Elapsed Time</TableHead>
                          <TableHead className="text-right">Break Time</TableHead>
                          <TableHead className="text-right">Efficiency</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {dropOffData.dropOffCandidates?.map((candidate, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-medium">{candidate.name}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="capitalize">
                                {candidate.region || 'N/A'}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Progress value={candidate.progress} className="w-16 h-2" />
                                <span className="text-sm">{candidate.progress}%</span>
                              </div>
                            </TableCell>
                            <TableCell className="text-right font-medium text-green-600">
                              {candidate.activeHours}h
                            </TableCell>
                            <TableCell className="text-right text-[#6b7280]">
                              {candidate.elapsedHours}h
                            </TableCell>
                            <TableCell className="text-right font-medium text-red-600">
                              {candidate.breakHours}h
                            </TableCell>
                            <TableCell className="text-right">
                              <Badge 
                                className={
                                  candidate.efficiency >= 70 ? "bg-green-100 text-green-700" :
                                  candidate.efficiency >= 30 ? "bg-yellow-100 text-yellow-700" :
                                  "bg-red-100 text-red-700"
                                }
                              >
                                {candidate.efficiency}%
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>

                {/* All Candidates Table */}
                <Card className="bg-white border-0 shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-[#3b3b3b]">All Candidates Time Analysis</CardTitle>
                    <CardDescription>Complete breakdown of active vs elapsed time</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="max-h-[400px] overflow-y-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Name</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead className="text-right">Responses</TableHead>
                            <TableHead className="text-right">Active</TableHead>
                            <TableHead className="text-right">Elapsed</TableHead>
                            <TableHead className="text-right"># Breaks</TableHead>
                            <TableHead className="text-right">Efficiency</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {dropOffData.allCandidates?.map((candidate, idx) => (
                            <TableRow key={idx}>
                              <TableCell className="font-medium">{candidate.name}</TableCell>
                              <TableCell>
                                <Badge 
                                  variant="outline"
                                  className={candidate.status === 'completed' ? 'border-green-500 text-green-600' : 'border-blue-500 text-blue-600'}
                                >
                                  {candidate.status}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-right">{candidate.responseCount}</TableCell>
                              <TableCell className="text-right text-green-600">{candidate.activeHours}h</TableCell>
                              <TableCell className="text-right text-[#6b7280]">{candidate.elapsedHours}h</TableCell>
                              <TableCell className="text-right">{candidate.numBreaks}</TableCell>
                              <TableCell className="text-right">
                                <span className={
                                  candidate.efficiency >= 70 ? "text-green-600 font-medium" :
                                  candidate.efficiency >= 30 ? "text-yellow-600" :
                                  "text-red-600"
                                }>
                                  {candidate.efficiency}%
                                </span>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ) : (
              <div className="text-center py-12">
                <TrendingDown className="w-12 h-12 mx-auto text-[#6b7280] mb-4" />
                <p className="text-[#6b7280]">No drop-off data available yet</p>
                <p className="text-sm text-[#6b7280] mt-1">Data will appear as candidates start assessments</p>
              </div>
            )}
          </TabsContent>

          {/* Question Time Analytics Tab */}
          <TabsContent value="question-time">
            {questionTimeLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-[#00AEEF]" />
              </div>
            ) : questionTimeData ? (
              <div className="space-y-6">
                {/* Summary Stats */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <Card className="bg-white border-0 shadow-sm">
                    <CardContent className="pt-4 pb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                          <Timer className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-[#3b3b3b]">{formatDuration(questionTimeData.summary?.overallAvgTime || 0)}</p>
                          <p className="text-xs text-[#6b7280]">Avg Time/Question</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-white border-0 shadow-sm">
                    <CardContent className="pt-4 pb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                          <Clock className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-[#3b3b3b]">{formatDuration(questionTimeData.summary?.overallMedianTime || 0)}</p>
                          <p className="text-xs text-[#6b7280]">Median Time</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-white border-0 shadow-sm">
                    <CardContent className="pt-4 pb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#00AEEF]/10 rounded-lg flex items-center justify-center">
                          <BarChart3 className="w-5 h-5 text-[#00AEEF]" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-[#3b3b3b]">{questionTimeData.summary?.totalDataPoints?.toLocaleString() || 0}</p>
                          <p className="text-xs text-[#6b7280]">Data Points</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-white border-0 shadow-sm">
                    <CardContent className="pt-4 pb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#63C465]/10 rounded-lg flex items-center justify-center">
                          <Activity className="w-5 h-5 text-[#63C465]" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-[#3b3b3b]">{questionTimeData.summary?.uniqueQuestions || 0}</p>
                          <p className="text-xs text-[#6b7280]">Questions Tracked</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Region Filter */}
                <div className="flex justify-end">
                  <Select value={regionFilter} onValueChange={setRegionFilter}>
                    <SelectTrigger className="w-[160px]">
                      <SelectValue placeholder="Filter by Region" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Regions</SelectItem>
                      <SelectItem value="jamaica">🇯🇲 Jamaica</SelectItem>
                      <SelectItem value="canada">🇨🇦 Canada</SelectItem>
                      <SelectItem value="usa">🇺🇸 USA</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* By Question Type */}
                  <Card className="bg-white border-0 shadow-sm">
                    <CardHeader>
                      <CardTitle className="text-[#3b3b3b] flex items-center gap-2">
                        <BarChart3 className="w-5 h-5 text-[#00AEEF]" />
                        Time by Question Type
                      </CardTitle>
                      <CardDescription>Average time spent on each input type</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {questionTimeData.byType && Object.entries(questionTimeData.byType).map(([type, data]) => {
                          const maxTime = Math.max(...Object.values(questionTimeData.byType).map(d => d.avgTime || 0), 1);
                          const percentage = (data.avgTime / maxTime) * 100;
                          return (
                            <div key={type} className="space-y-1">
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-[#3b3b3b] capitalize font-medium">{type.replace('_', ' ')}</span>
                                <span className="text-[#6b7280]">{formatDuration(data.avgTime)} avg</span>
                              </div>
                              <div className="h-3 bg-[#f0f0f0] rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-gradient-to-r from-[#00AEEF] to-[#0077B5] rounded-full transition-all"
                                  style={{ width: `${percentage}%` }}
                                />
                              </div>
                              <p className="text-xs text-[#6b7280]">{data.totalResponses} responses</p>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>

                  {/* By Region */}
                  <Card className="bg-white border-0 shadow-sm">
                    <CardHeader>
                      <CardTitle className="text-[#3b3b3b] flex items-center gap-2">
                        <Users className="w-5 h-5 text-[#00AEEF]" />
                        Time by Region
                      </CardTitle>
                      <CardDescription>Average time spent per region</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {questionTimeData.byRegion && Object.entries(questionTimeData.byRegion).map(([region, data]) => {
                          const maxTime = Math.max(...Object.values(questionTimeData.byRegion).map(d => d.avgTime || 0), 1);
                          const percentage = (data.avgTime / maxTime) * 100;
                          const flagEmoji = region === 'jamaica' ? '🇯🇲' : region === 'canada' ? '🇨🇦' : region === 'usa' ? '🇺🇸' : '🌍';
                          return (
                            <div key={region} className="space-y-1">
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-[#3b3b3b] capitalize font-medium">{flagEmoji} {region}</span>
                                <span className="text-[#6b7280]">{formatDuration(data.avgTime)} avg</span>
                              </div>
                              <div className="h-3 bg-[#f0f0f0] rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-gradient-to-r from-[#63C465] to-[#3da33f] rounded-full transition-all"
                                  style={{ width: `${percentage}%` }}
                                />
                              </div>
                              <p className="text-xs text-[#6b7280]">{data.totalResponses} responses • {data.uniqueQuestions} questions</p>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Slowest Questions */}
                  <Card className="bg-white border-0 shadow-sm">
                    <CardHeader>
                      <CardTitle className="text-[#3b3b3b] flex items-center gap-2">
                        <Flame className="w-5 h-5 text-orange-500" />
                        Slowest Questions (Bottlenecks)
                      </CardTitle>
                      <CardDescription>Questions that take the longest to answer</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 max-h-[300px] overflow-y-auto">
                        {questionTimeData.slowestQuestions?.slice(0, 10).map((q, idx) => (
                          <div key={q.questionId} className="flex items-center justify-between p-3 bg-[#f8f9fa] rounded-lg">
                            <div className="flex items-center gap-3">
                              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                                idx < 3 ? 'bg-orange-100 text-orange-600' : 'bg-gray-100 text-gray-600'
                              }`}>
                                {idx + 1}
                              </span>
                              <div>
                                <p className="text-sm font-medium text-[#3b3b3b]">{q.questionId}</p>
                                <p className="text-xs text-[#6b7280]">{q.responses} responses</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-bold text-orange-600">{formatDuration(q.avgTime)}</p>
                              <p className="text-xs text-[#6b7280]">median: {formatDuration(q.medianTime)}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Fastest Questions */}
                  <Card className="bg-white border-0 shadow-sm">
                    <CardHeader>
                      <CardTitle className="text-[#3b3b3b] flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-[#63C465]" />
                        Fastest Questions
                      </CardTitle>
                      <CardDescription>Questions answered most quickly</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 max-h-[300px] overflow-y-auto">
                        {questionTimeData.fastestQuestions?.slice(0, 10).map((q, idx) => (
                          <div key={q.questionId} className="flex items-center justify-between p-3 bg-[#f8f9fa] rounded-lg">
                            <div className="flex items-center gap-3">
                              <span className="w-6 h-6 rounded-full bg-green-100 text-green-600 flex items-center justify-center text-xs font-bold">
                                {idx + 1}
                              </span>
                              <div>
                                <p className="text-sm font-medium text-[#3b3b3b]">{q.questionId}</p>
                                <p className="text-xs text-[#6b7280]">{q.responses} responses</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-bold text-[#63C465]">{formatDuration(q.avgTime)}</p>
                              <p className="text-xs text-[#6b7280]">median: {formatDuration(q.medianTime)}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Heatmap */}
                <Card className="bg-white border-0 shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-[#3b3b3b] flex items-center gap-2">
                      <Activity className="w-5 h-5 text-[#00AEEF]" />
                      Time Intensity Heatmap
                    </CardTitle>
                    <CardDescription>Visual representation of time spent per question (darker = more time)</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-1">
                      {questionTimeData.heatmapData?.map((item) => {
                        const intensity = item.intensity || 0;
                        const bgColor = intensity > 80 ? 'bg-red-500' :
                                       intensity > 60 ? 'bg-orange-500' :
                                       intensity > 40 ? 'bg-yellow-500' :
                                       intensity > 20 ? 'bg-green-400' : 'bg-green-200';
                        return (
                          <div
                            key={item.questionId}
                            className={`w-8 h-8 ${bgColor} rounded cursor-pointer hover:ring-2 hover:ring-[#00AEEF] transition-all`}
                            title={`${item.questionId}: ${formatDuration(item.avgTime)} avg (${item.responses} responses)`}
                          />
                        );
                      })}
                    </div>
                    <div className="flex items-center gap-4 mt-4 text-xs text-[#6b7280]">
                      <span className="flex items-center gap-1"><span className="w-3 h-3 bg-green-200 rounded"></span> Fast</span>
                      <span className="flex items-center gap-1"><span className="w-3 h-3 bg-yellow-500 rounded"></span> Medium</span>
                      <span className="flex items-center gap-1"><span className="w-3 h-3 bg-red-500 rounded"></span> Slow</span>
                    </div>
                  </CardContent>
                </Card>

                {/* Full Table */}
                <Card className="bg-white border-0 shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-[#3b3b3b] flex items-center gap-2">
                      <ArrowUpDown className="w-5 h-5 text-[#00AEEF]" />
                      All Questions (Sortable)
                    </CardTitle>
                    <CardDescription>Complete list of all tracked questions</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                      <Table>
                        <TableHeader className="sticky top-0 bg-white">
                          <TableRow>
                            <TableHead className="cursor-pointer hover:bg-gray-50" onClick={() => {
                              setQuestionSortField('questionId');
                              setQuestionSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
                            }}>
                              Question ID {questionSortField === 'questionId' && (questionSortDir === 'asc' ? '↑' : '↓')}
                            </TableHead>
                            <TableHead className="cursor-pointer hover:bg-gray-50" onClick={() => {
                              setQuestionSortField('avgTime');
                              setQuestionSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
                            }}>
                              Avg Time {questionSortField === 'avgTime' && (questionSortDir === 'asc' ? '↑' : '↓')}
                            </TableHead>
                            <TableHead className="cursor-pointer hover:bg-gray-50" onClick={() => {
                              setQuestionSortField('medianTime');
                              setQuestionSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
                            }}>
                              Median {questionSortField === 'medianTime' && (questionSortDir === 'asc' ? '↑' : '↓')}
                            </TableHead>
                            <TableHead>Min</TableHead>
                            <TableHead>Max</TableHead>
                            <TableHead className="cursor-pointer hover:bg-gray-50" onClick={() => {
                              setQuestionSortField('responses');
                              setQuestionSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
                            }}>
                              Responses {questionSortField === 'responses' && (questionSortDir === 'asc' ? '↑' : '↓')}
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {questionTimeData.allQuestions
                            ?.sort((a, b) => {
                              const aVal = a[questionSortField];
                              const bVal = b[questionSortField];
                              if (questionSortDir === 'asc') {
                                return aVal > bVal ? 1 : -1;
                              }
                              return aVal < bVal ? 1 : -1;
                            })
                            .slice(0, 100)
                            .map((q) => (
                            <TableRow key={q.questionId}>
                              <TableCell className="font-mono text-sm">{q.questionId}</TableCell>
                              <TableCell className="font-bold">{formatDuration(q.avgTime)}</TableCell>
                              <TableCell>{formatDuration(q.medianTime)}</TableCell>
                              <TableCell className="text-[#63C465]">{formatDuration(q.minTime)}</TableCell>
                              <TableCell className="text-orange-600">{formatDuration(q.maxTime)}</TableCell>
                              <TableCell>{q.responses}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ) : (
              <div className="text-center py-12">
                <Hourglass className="w-12 h-12 mx-auto mb-4 text-[#6b7280] opacity-40" />
                <p className="text-[#6b7280]">No question time data available yet</p>
                <p className="text-sm text-[#6b7280] mt-1">Data will appear as candidates complete assessments</p>
              </div>
            )}
          </TabsContent>

          {/* Sessions Tab */}
          <TabsContent value="sessions">
            <Card className="bg-white border-0 shadow-sm">
              <CardHeader>
                <div className="flex flex-col sm:flex-row gap-4 justify-between">
                  <div>
                    <CardTitle className="text-[#3b3b3b]">User Sessions</CardTitle>
                    <CardDescription>Detailed view of all user sessions</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Select value={regionFilter} onValueChange={setRegionFilter}>
                      <SelectTrigger className="w-[160px]" data-testid="region-filter-sessions">
                        <SelectValue placeholder="Select Region" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Regions</SelectItem>
                        <SelectItem value="jamaica">🇯🇲 Jamaica</SelectItem>
                        <SelectItem value="usa">🇺🇸 USA</SelectItem>
                        <SelectItem value="canada">🇨🇦 Canada</SelectItem>
                        <SelectItem value="barbados">🇧🇧 Barbados</SelectItem>
                      <SelectItem value="trinidad_tobago">🇹🇹 Trinidad & Tobago</SelectItem>
                      <SelectItem value="panama">🇵🇦 Panama</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={userTypeFilter} onValueChange={(v) => { setUserTypeFilter(v); setSessionsPage(1); }}>
                      <SelectTrigger className="w-[160px]" data-testid="user-type-filter">
                        <SelectValue placeholder="User Type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Users</SelectItem>
                        <SelectItem value="candidate">Candidates</SelectItem>
                        <SelectItem value="admin">Admins</SelectItem>
                        <SelectItem value="anonymous">Anonymous</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {sessions.length === 0 ? (
                  <div className="text-center py-12 text-[#6b7280]">
                    <Users className="w-12 h-12 mx-auto mb-4 opacity-40" />
                    <p>No sessions found</p>
                  </div>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>User</TableHead>
                            <TableHead>Type</TableHead>
                            <TableHead>Device</TableHead>
                            <TableHead>Duration</TableHead>
                            <TableHead>Pages</TableHead>
                            <TableHead>Actions</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Started</TableHead>
                            <TableHead></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {sessions.map((session) => (
                            <TableRow 
                              key={session.id} 
                              className={`cursor-pointer hover:bg-[#f8f9fa] ${selectedSession?.id === session.id ? 'bg-[#00AEEF]/5' : ''}`}
                              onClick={() => handleSessionClick(session)}
                              data-testid={`session-row-${session.id}`}
                            >
                              <TableCell>
                                <div>
                                  <p className="font-medium text-[#3b3b3b]">{session.userName}</p>
                                  {session.userEmail && (
                                    <p className="text-xs text-[#6b7280]">{session.userEmail}</p>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>
                                <Badge variant="outline" className="capitalize">
                                  {session.userType}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-1">
                                  {session.deviceType === 'mobile' ? (
                                    <Smartphone className="w-4 h-4 text-[#6b7280]" />
                                  ) : session.deviceType === 'tablet' ? (
                                    <Tablet className="w-4 h-4 text-[#6b7280]" />
                                  ) : (
                                    <Monitor className="w-4 h-4 text-[#6b7280]" />
                                  )}
                                  <span className="text-xs text-[#6b7280]">{session.browser || 'Unknown'}</span>
                                </div>
                              </TableCell>
                              <TableCell>
                                <span className="font-medium">{formatDuration(session.totalDuration)}</span>
                              </TableCell>
                              <TableCell>
                                <span className="text-[#00AEEF] font-medium">{session.pageViews}</span>
                              </TableCell>
                              <TableCell>
                                <span>{session.actionsCount}</span>
                              </TableCell>
                              <TableCell>
                                {session.isActive ? (
                                  <Badge className="bg-[#63C465]/10 text-[#63C465] hover:bg-[#63C465]/10">
                                    Active
                                  </Badge>
                                ) : session.endedAt ? (
                                  <Badge variant="outline" className="text-[#6b7280]">
                                    Ended
                                  </Badge>
                                ) : (
                                  <Badge variant="outline" className="text-orange-600 border-orange-200">
                                    Idle
                                  </Badge>
                                )}
                              </TableCell>
                              <TableCell>
                                <span className="text-sm text-[#6b7280]">{formatTimeAgo(session.startedAt)}</span>
                              </TableCell>
                              <TableCell>
                                <Button variant="ghost" size="sm">
                                  <ExternalLink className="w-4 h-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                    
                    {/* Pagination */}
                    {sessionsPagination.totalPages > 1 && (
                      <div className="flex items-center justify-between mt-4 pt-4 border-t">
                        <p className="text-sm text-[#6b7280]">
                          Page {sessionsPagination.page} of {sessionsPagination.totalPages} ({sessionsPagination.total} total)
                        </p>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setSessionsPage(p => Math.max(1, p - 1))}
                            disabled={sessionsPage === 1}
                          >
                            <ChevronLeft className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setSessionsPage(p => Math.min(sessionsPagination.totalPages, p + 1))}
                            disabled={sessionsPage === sessionsPagination.totalPages}
                          >
                            <ChevronRight className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            {/* Session Detail Drawer */}
            {selectedSession && (
              <Card className="bg-white border-0 shadow-sm mt-6">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-[#3b3b3b]">Session Details</CardTitle>
                      <CardDescription>
                        {selectedSession.userName} - {formatTimeAgo(selectedSession.startedAt)}
                      </CardDescription>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => setSelectedSession(null)}>
                      Close
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                    <div className="p-3 bg-[#f8f9fa] rounded-lg">
                      <p className="text-xs text-[#6b7280]">Duration</p>
                      <p className="font-bold text-[#3b3b3b]">{formatDuration(selectedSession.totalDuration)}</p>
                    </div>
                    <div className="p-3 bg-[#f8f9fa] rounded-lg">
                      <p className="text-xs text-[#6b7280]">Page Views</p>
                      <p className="font-bold text-[#00AEEF]">{selectedSession.pageViews}</p>
                    </div>
                    <div className="p-3 bg-[#f8f9fa] rounded-lg">
                      <p className="text-xs text-[#6b7280]">Actions</p>
                      <p className="font-bold text-[#3b3b3b]">{selectedSession.actionsCount}</p>
                    </div>
                    <div className="p-3 bg-[#f8f9fa] rounded-lg">
                      <p className="text-xs text-[#6b7280]">Errors</p>
                      <p className={`font-bold ${selectedSession.errorsCount > 0 ? 'text-red-600' : 'text-[#63C465]'}`}>
                        {selectedSession.errorsCount}
                      </p>
                    </div>
                  </div>
                  
                  {selectedSession.assessmentToken && (
                    <div className="mb-6 p-3 bg-[#00AEEF]/5 rounded-lg">
                      <p className="text-xs text-[#6b7280]">Assessment Token</p>
                      <p className="font-mono text-sm text-[#3b3b3b]">{selectedSession.assessmentToken}</p>
                      {selectedSession.initialProgress !== null && (
                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-xs text-[#6b7280]">Progress:</span>
                          <span className="text-sm">{selectedSession.initialProgress}%</span>
                          {selectedSession.finalProgress !== null && (
                            <>
                              <span className="text-xs text-[#6b7280]">→</span>
                              <span className="text-sm font-medium text-[#63C465]">{selectedSession.finalProgress}%</span>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  
                  <div>
                    <h4 className="text-sm font-medium text-[#3b3b3b] mb-3">Activity Timeline</h4>
                    {sessionActivities.length === 0 ? (
                      <p className="text-sm text-[#6b7280]">No detailed activities recorded</p>
                    ) : (
                      <div className="space-y-2 max-h-[300px] overflow-y-auto">
                        {sessionActivities.map((activity, index) => (
                          <div key={activity.id || index} className="flex items-start gap-3 p-2 hover:bg-[#f8f9fa] rounded">
                            <div className={`w-2 h-2 mt-2 rounded-full ${
                              activity.eventType === 'error' ? 'bg-red-500' :
                              activity.eventType === 'action' ? 'bg-[#00AEEF]' :
                              'bg-[#63C465]'
                            }`} />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-[#3b3b3b]">{activity.eventName}</span>
                                <Badge variant="outline" className="text-xs">{activity.eventType}</Badge>
                              </div>
                              {activity.pageUrl && (
                                <p className="text-xs text-[#6b7280] truncate">{activity.pageUrl}</p>
                              )}
                              {activity.errorMessage && (
                                <p className="text-xs text-red-600 mt-1">{activity.errorMessage}</p>
                              )}
                            </div>
                            <span className="text-xs text-[#6b7280] whitespace-nowrap">
                              {formatTimeAgo(activity.createdAt)}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Errors Tab */}
          <TabsContent value="errors">
            <Card className="bg-white border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="text-[#3b3b3b] flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                  Recent Errors & Issues
                </CardTitle>
                <CardDescription>Track and resolve user-facing errors</CardDescription>
              </CardHeader>
              <CardContent>
                {analytics?.recentErrors && analytics.recentErrors.length > 0 ? (
                  <div className="space-y-3">
                    {analytics.recentErrors.map((error, index) => (
                      <div key={error.id || index} className="p-4 bg-red-50 border border-red-100 rounded-lg">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <AlertTriangle className="w-4 h-4 text-red-500" />
                              <span className="font-medium text-red-700">{error.errorMessage || 'Unknown Error'}</span>
                            </div>
                            <p className="text-sm text-[#6b7280]">
                              User: {error.userName} ({error.userEmail || 'No email'})
                            </p>
                            {error.pageUrl && (
                              <p className="text-xs text-[#6b7280] truncate">Page: {error.pageUrl}</p>
                            )}
                          </div>
                          <span className="text-xs text-[#6b7280] whitespace-nowrap">
                            {formatTimeAgo(error.createdAt)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-[#63C465]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <AlertTriangle className="w-8 h-8 text-[#63C465]" />
                    </div>
                    <p className="text-[#63C465] font-medium">No errors recorded!</p>
                    <p className="text-sm text-[#6b7280] mt-1">The platform is running smoothly</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

